/**
 * Lu or Labu — the rules, with no DOM in sight.
 *
 * Each round deals a grid of plush dolls. Some are real Labubu, most are not ("lafufu":
 * counterfeits and lookalikes). The player must find every real one — except that some
 * rounds hold two, and some hold none at all, which is the whole trick. There is no
 * confirm step: tapping a doll commits that choice the instant your finger lands.
 *
 *   tap a real Labubu      → found. Found them all and the round is won.
 *   tap a lafufu           → mistake: penalty, round over.
 *   "no Labubu" when right → the round is won.
 *   "no Labubu" when wrong → mistake: penalty, round over.
 *   run out of window      → mistake: penalty, round over.
 *
 * Holding a tile to inspect its face is a view concern and costs nothing but the clock —
 * which is the point, because the clock is the score.
 *
 * Everything is driven by a `now` (ms) the caller passes in, so it is testable and pause-able.
 *
 * `seeded` and `formatTime` are deliberately re-declared here rather than imported from
 * tacoslap: a game is meant to be one self-contained folder. If a third game wants them
 * they should move to a shared util, not cross-import between games.
 */

export type Tier = 'easy' | 'medium' | 'hard';

/** one picture in the pool, as the manifest describes it */
export interface Tile {
  id: string;
  src: string;
  /** tight head crop; the inspect view uses it when present */
  face: string | null;
  isLabubu: boolean;
  /** how hard this one is to tell from a real Labubu; null for the real ones */
  tier: Tier | null;
  /** the doll's own retail box is in frame, so the brand gives the answer away */
  brandVisible: boolean;
}

export interface Rules {
  rounds: number;
  /** dolls dealt per round */
  tiles: number;
  /** ms allowed to answer a round */
  windowMs: number;
  /** ms added to the clock for a wrong answer or a timeout */
  penaltyMs: number;
  /** how many of the rounds hold no Labubu at all */
  zeroRounds: number;
  /** how many of the rounds hold two */
  doubleRounds: number;
}

export const DEFAULT_RULES: Rules = {
  rounds: 12,
  tiles: 6,
  windowMs: 5000,
  penaltyMs: 2000,
  zeroRounds: 2,
  doubleRounds: 3,
};

export interface Round {
  tiles: Tile[];
  /** indices into `tiles` that are real Labubu */
  targets: number[];
  tier: Tier;
}

export type Status = 'idle' | 'playing' | 'won';
export type Did = 'pick' | 'none' | 'timeout';

export interface Outcome {
  ok: boolean;
  did: Did;
  /** the tile tapped, when did === 'pick' */
  index?: number;
  /** what the round actually held */
  targets: number[];
  /** right or wrong, this round is finished */
  roundOver: boolean;
  /** real Labubu still unfound this round */
  left: number;
  won: boolean;
}

export interface Snapshot {
  status: Status;
  round: number;
  rounds: number;
  tiles: Tile[];
  found: number[];
  targets: number;
  mistakes: number;
  streak: number;
}

/** the tier of decoy a round deals: the grid gets harder as you go */
export function tierFor(round: number, rounds: number): Tier {
  const t = round / Math.max(1, rounds);
  if (t < 1 / 3) return 'easy';
  if (t < 2 / 3) return 'medium';
  return 'hard';
}

function shuffle<T>(xs: T[], rng: () => number): T[] {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** n distinct items from xs; falls back to repeats only if xs is smaller than n */
function pick<T>(xs: T[], n: number, rng: () => number): T[] {
  const bag = shuffle(xs, rng);
  const out = bag.slice(0, n);
  while (out.length < n && bag.length) out.push(bag[out.length % bag.length]);
  return out;
}

/** how many real Labubu each round holds: mostly one, a few two, a few none */
export function targetCounts(rules: Rules, rng: () => number): number[] {
  const counts = new Array(rules.rounds).fill(1);
  // never open on a trick round — the player needs one honest round to learn the grid
  const slots = shuffle(
    Array.from({ length: rules.rounds - 1 }, (_, i) => i + 1),
    rng,
  );
  let at = 0;
  for (let i = 0; i < rules.doubleRounds && at < slots.length; i++) counts[slots[at++]] = 2;
  for (let i = 0; i < rules.zeroRounds && at < slots.length; i++) counts[slots[at++]] = 0;
  return counts;
}

export function buildRounds(pool: Tile[], rules: Rules, rng: () => number = Math.random): Round[] {
  const labubu = pool.filter((t) => t.isLabubu);
  const lafufu = pool.filter((t) => !t.isLabubu);
  if (!labubu.length || !lafufu.length) throw new Error('pool needs both Labubu and lafufu');

  const byTier: Record<Tier, Tile[]> = { easy: [], medium: [], hard: [] };
  for (const t of lafufu) byTier[t.tier ?? 'hard'].push(t);
  // a tier with too few pictures borrows from the next one along
  const order: Tier[] = ['easy', 'medium', 'hard'];
  const decoysFor = (tier: Tier, n: number): Tile[] => {
    const seen = new Set<string>();
    const bag: Tile[] = [];
    for (const t of [tier, ...order.filter((o) => o !== tier)]) {
      for (const tile of byTier[t]) if (!seen.has(tile.id)) (seen.add(tile.id), bag.push(tile));
      if (bag.length >= n) break;
    }
    return pick(bag, n, rng);
  };

  const counts = targetCounts(rules, rng);
  return counts.map((count, r) => {
    const tier = tierFor(r, rules.rounds);
    const tiles = shuffle([...pick(labubu, count, rng), ...decoysFor(tier, rules.tiles - count)], rng);
    const targets = tiles.flatMap((t, i) => (t.isLabubu ? [i] : []));
    return { tiles, targets, tier };
  });
}

export class LuOrLabu {
  readonly rules: Rules;
  private rounds: Round[] = [];
  private index = 0;
  private found = new Set<number>();
  private startAt = 0;
  private roundAt = 0;
  private penalties = 0;
  private pausedTotal = 0;
  private pausedAt: number | null = null;
  private status: Status = 'idle';
  private prepared = false;
  private readonly pool: Tile[];
  private rng: () => number;
  mistakes = 0;
  streak = 0;
  bestStreak = 0;

  constructor(pool: Tile[], rules: Partial<Rules> = {}, rng: () => number = Math.random) {
    this.pool = pool;
    this.rules = { ...DEFAULT_RULES, ...rules };
    this.rng = rng;
  }

  get round(): Round | null {
    return this.rounds[this.index] ?? null;
  }

  get roundIndex(): number {
    return this.index;
  }

  snapshot(): Snapshot {
    const r = this.round;
    return {
      status: this.status,
      round: this.index,
      rounds: this.rules.rounds,
      tiles: r?.tiles ?? [],
      found: [...this.found],
      targets: r?.targets.length ?? 0,
      mistakes: this.mistakes,
      streak: this.streak,
    };
  }

  /**
   * Deal the rounds without starting the clock, so the view can preload their pictures.
   * Optional: start() deals for itself if nobody called this first.
   */
  prepare(): Round[] {
    this.rounds = buildRounds(this.pool, this.rules, this.rng);
    this.prepared = true;
    return this.rounds;
  }

  start(now: number): void {
    if (!this.prepared) this.prepare();
    this.prepared = false; // the next start deals a fresh set
    this.index = 0;
    this.found.clear();
    this.mistakes = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.penalties = 0;
    this.pausedTotal = 0;
    this.pausedAt = null;
    this.startAt = now;
    this.roundAt = now;
    this.status = 'playing';
  }

  /** the running clock: wall time minus pauses plus penalties */
  elapsed(now: number): number {
    if (this.status === 'idle') return 0;
    const end = this.pausedAt ?? now;
    return Math.max(0, end - this.startAt - this.pausedTotal + this.penalties);
  }

  /** 1 → the round just opened, 0 → out of time */
  remaining(now: number): number {
    if (this.status !== 'playing' || this.pausedAt !== null) return 1;
    return Math.max(0, 1 - (now - this.roundAt) / this.rules.windowMs);
  }

  isFound(i: number): boolean {
    return this.found.has(i);
  }

  pause(now: number): void {
    if (this.status === 'playing' && this.pausedAt === null) this.pausedAt = now;
  }

  resume(now: number): void {
    if (this.pausedAt !== null) {
      const gap = now - this.pausedAt;
      this.pausedTotal += gap;
      this.roundAt += gap; // the window picks up where it left off
      this.pausedAt = null;
    }
  }

  get paused(): boolean {
    return this.pausedAt !== null;
  }

  /** call every frame; returns an outcome when the window has closed */
  tick(now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null) return null;
    if (now - this.roundAt < this.rules.windowMs) return null;
    return this.resolve('timeout', undefined, now);
  }

  pick(i: number, now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null) return null;
    const r = this.round;
    if (!r || i < 0 || i >= r.tiles.length || this.found.has(i)) return null;
    return this.resolve('pick', i, now);
  }

  declareNone(now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null) return null;
    return this.resolve('none', undefined, now);
  }

  private resolve(did: Did, index: number | undefined, now: number): Outcome {
    const r = this.round!;
    let ok: boolean;
    if (did === 'pick') ok = r.tiles[index!].isLabubu;
    else if (did === 'none') ok = r.targets.length === 0;
    else ok = false;

    // a right pick that does not finish the round leaves the window running
    if (ok && did === 'pick') {
      this.found.add(index!);
      if (this.found.size < r.targets.length) {
        this.streak++;
        this.bestStreak = Math.max(this.bestStreak, this.streak);
        return { ok: true, did, index, targets: r.targets, roundOver: false, left: r.targets.length - this.found.size, won: false };
      }
    }

    if (ok) {
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
    } else {
      this.mistakes++;
      this.streak = 0;
      this.penalties += this.rules.penaltyMs;
    }

    const targets = r.targets;
    const won = this.advance(now);
    return { ok, did, index, targets, roundOver: true, left: 0, won };
  }

  /** move to the next round; true when that was the last one */
  private advance(now: number): boolean {
    this.index++;
    this.found.clear();
    this.roundAt = now;
    if (this.index >= this.rounds.length) {
      this.status = 'won';
      return true;
    }
    return false;
  }
}

/** a tiny seeded RNG (mulberry32) for tests and replays */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// score formatting is shared; re-exported so this game's own modules keep importing from here
export { formatTime } from '../../ui/format.ts';
